import React, { useCallback, useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Tappable } from '@vectord/components';
import { TypeUtils } from '@vectord/utils';
import cogoToast from 'cogo-toast';
import classNames from 'classnames';

import {
  EDIT_SBAT_GROUP_PAGE_TRACKINGS,
  hasContestLesson,
  isHybridContest,
  newDateOfTopicSelector,
  rescheduleSbatGroupCommunicationsSelector,
  rescheduleReasonSelector,
  resetRescheduleModal,
  RESCHEDULE_REASON_MODAL_STAGES,
  setRescheduleModalStage,
  setRescheduleSbatGroupErrors,
  selectedSbatGroupSelector,
  useSbatGroupConfirmation,
  useUpdateSbatGroupMutation,
} from 'features/classCreation';
import analytics from 'utils/analytics';
import FieldErrors from 'components/FieldErrors';
import styles from './RescheduleModalConfirmStage.module.scss';

export default function RescheduleModalConfirmStage() {
  const dispatch = useDispatch();
  const communications = useSelector(rescheduleSbatGroupCommunicationsSelector);
  const rescheduleReason = useSelector(rescheduleReasonSelector);
  const newDateOfTopic = useSelector(newDateOfTopicSelector);
  const sbatGroup = useSelector(selectedSbatGroupSelector);
  const [updateAcademyTopic, { isSuccess, error, isLoading: isUpdating }] =
    useUpdateSbatGroupMutation();

  const handleCancel = useCallback(() => {
    dispatch(setRescheduleModalStage(RESCHEDULE_REASON_MODAL_STAGES.notOpen));
  }, [dispatch]);

  const handleSbatGroupUpdate = useCallback(() => {
    const {
      academy_topic: { title, lessons },
    } = sbatGroup;
    let hybridContestNames = [];
    let contestNames = [];
    const comms = Object.keys(communications).filter(
      (key) => communications[key]
    );
    const superBatchNames = sbatGroup.super_batch_academy_topics.map(
      (sbat) => sbat.super_batch.name
    );
    if (isHybridContest(lessons)) {
      hybridContestNames.push(title);
    }
    if (hasContestLesson(lessons)) {
      contestNames.push(title);
    }

    analytics.click({
      click_type: EDIT_SBAT_GROUP_PAGE_TRACKINGS.rescheduleClass,
      custom: {
        super_batch_names: superBatchNames,
        hybrid_contest_count: hybridContestNames.length,
        hybrid_contest_name: hybridContestNames,
        contest_count: contestNames.length,
        contest_name: contestNames,
      },
    });
    updateAcademyTopic({
      sbatGroup: sbatGroup,
      newDateOfTopic,
      reason: rescheduleReason?.customReason || rescheduleReason?.label,
      notificationChannels: comms,
    });
  }, [
    communications,
    newDateOfTopic,
    rescheduleReason,
    sbatGroup,
    updateAcademyTopic,
  ]);

  const handleProceed = useSbatGroupConfirmation(
    handleSbatGroupUpdate,
    sbatGroup
  );

  useEffect(() => {
    if (isSuccess) {
      cogoToast.success('Class date and time is updated successfully!');
      batch(() => {
        dispatch(resetRescheduleModal());
        dispatch(setRescheduleSbatGroupErrors(null));
      });
    }
  }, [dispatch, isSuccess]);

  useEffect(() => {
    if (error) {
      const errors = error.data?.message;
      if (TypeUtils.isString(errors)) {
        cogoToast.error(error?.data?.message);
      } else {
        dispatch(setRescheduleSbatGroupErrors({ base: errors.base }));
        cogoToast.error(
          'There was some problem in updating the class date and time. Please try again later!'
        );
      }
    }
  }, [dispatch, error]);

  return (
    <div className="column justify-c align-c">
      <div className="m-b-m dark h4">
        Are you sure you want to update the schedule of selected class?
      </div>
      <FieldErrors
        errors={error?.data?.message}
        name="base"
        className={styles.errors}
      />
      <Tappable
        onClick={handleProceed}
        disabled={isUpdating}
        className={classNames(
          styles.button,
          'btn btn-primary btn-long h4 m-b-xs'
        )}
      >
        Yes, I’d like to proceed
      </Tappable>
      <Tappable
        onClick={handleCancel}
        disabled={isUpdating}
        className={classNames(
          styles.button,
          'btn btn-primary btn-outlined btn-long h4'
        )}
      >
        No, I clicked on it by mistake
      </Tappable>
    </div>
  );
}
